@import "@/scss/use-bootstrap-mixins.scss";

.mobile {
  display: flex !important;

  @include media-breakpoint-up(lg) {
    display: none !important;
  }
}

.desktop {
  display: none !important;
  @include media-breakpoint-up(lg) {
    display: flex !important;
  }
}

.card {
  background: #fff;
  border: 1px solid #d7d7d7;
  border-radius: 20px;
  padding: 15px;
  display: flex;
  flex-direction: column;
  gap: 18px;

  @include media-breakpoint-up(lg) {
    flex-direction: row;
    align-items: stretch;
    gap: 20px;
  }

  // Image + carousel dots
  .imageGallery {
    display: flex;
    flex-direction: column;
    width: 100%;

    @include media-breakpoint-up(lg) {
      width: 234px;
      flex: 0 0 234px;

      // Fixed image height — do not stretch with a taller card.
      align-self: flex-start;
    }
  }

  .imageWrapper {
    position: relative;
    width: 100%;
    height: 250px;
    background: #f5f5f5;
    border-radius: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px 20px 40px;
    overflow: hidden;

    .picture {
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
      display: block;
      pointer-events: none;
    }
  }

  .dots {
    position: absolute;
    bottom: 16px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    gap: 15px;
    align-items: center;
    z-index: 2;
  }

  .dot {
    // height must be !important: the app root sets `#rapidstor-v2-frontend
    // button { height: initial }` (ID specificity) which would otherwise
    // collapse these empty buttons.
    width: 8px !important;
    height: 8px !important;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: $primaryLight;
    cursor: pointer;
    transition: background 0.15s ease;

    &:focus-visible {
      outline: 2px solid $primary;
      outline-offset: 2px;
    }
  }

  .dotActive {
    background: $primary;
  }

  .unitTypeDetails {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 18px;

    @include media-breakpoint-up(lg) {
      gap: 20px;
      padding-right: 20px;
      border-right: 1px solid #d7d7d7;
    }

    .titleRow {
      display: flex;
      align-items: center;
      gap: 12px;
      flex-wrap: wrap;
    }

    // !important throughout this file guards against the app root's
    // `#rapidstor-v2-frontend span { line-height: normal; text-align: revert;
    // align-items: revert; font-weight: revert; letter-spacing: normal }` reset,
    // which wins on ID specificity over these scoped classes.
    .title {
      font-size: 20px;
      font-weight: 500 !important;
      line-height: 1.3 !important;
      color: #0f0f0f;

      @include media-breakpoint-up(lg) {
        font-size: 25px;
      }
    }

    .sizeBadge {
      background: #ebebeb;
      color: #000;
      font-size: 10px;
      letter-spacing: 0.19px !important;
      padding: 3px 10px;
      border-radius: 20px;
      white-space: nowrap;
    }

    // Meta fields (Square metres / Size / Type / Level) spread evenly.
    .features {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      gap: 12px;
      width: 100%;
      flex-wrap: wrap;

      .block {
        display: flex;
        flex: 0 1 auto;
        flex-direction: column;
        gap: 4px;

        .label {
          font-size: 12px;
          font-weight: 500 !important;
          color: #0f0f0f;
          line-height: 1.2 !important;
        }

        .value {
          font-size: 12px;
          font-weight: 400 !important;
          color: #808080;
          line-height: 1.2 !important;
        }
      }
    }

    .description {
      display: flex;
      flex-direction: column;
      gap: 8px;

      .header {
        font-size: 15px;
        font-weight: 500 !important;
        color: #0f0f0f;

        @include media-breakpoint-up(lg) {
          font-size: 12px;
        }
      }

      .body {
        font-size: 12px;
        font-weight: 400 !important;
        color: #808080;
        line-height: 1.4 !important;
      }
    }

    // Feature / benefit icon list — each item occupies a quarter of the row.
    .tags {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      row-gap: 16px;
      width: 100%;

      // .tag / .iconWrap are spans, so the app root's
      // `#rapidstor-v2-frontend span { align-items: revert; text-align: revert }`
      // reset applies — force the centring with !important.
      .tag {
        display: flex;
        flex: 0 0 25%;
        max-width: 25%;
        flex-direction: column;
        align-items: center !important;
        justify-content: flex-start;
        gap: 8px;
        text-align: center !important;

        .iconWrap {
          display: flex;
          align-items: center !important;
          justify-content: center;
          width: 40px;
          height: 40px;
          border-radius: 30px;
          background: $primaryLightest;

          @include media-breakpoint-up(lg) {
            width: auto;
            height: auto;
            background: transparent;
          }
        }

        .icon {
          color: $primary;
          font-size: 18px;
          height: 20px;
        }

        // line-height / text-align / font-weight need !important: the app root
        // resets `#rapidstor-v2-frontend span { line-height: normal;
        // text-align: revert; font-weight: revert }` at ID specificity.
        .content {
          width: 100%;
          font-size: 12px;
          font-weight: 400 !important;
          color: #0f0f0f;
          line-height: 1.4 !important;
          text-align: center !important;
        }
      }
    }

    .variantSelector {
      margin: 0;
      border: none;
      padding: 0;

      .variantOptions {
        display: flex;
        gap: 8px;
        flex-wrap: wrap;
      }

      .variantOption {
        display: flex;
        align-items: center;
        gap: 4px;
        font-size: 12px;
        position: relative;
        cursor: pointer;
        line-height: 1;

        input[type="radio"] {
          position: absolute;
          opacity: 0;
          width: 0;
          height: 0;
          margin: 0;
        }

        span {
          display: inline-flex;
          align-items: center !important;
          gap: 6px;
          padding: 6px 12px;
          background: #fff;
          border: 1px solid $primaryLightest;
          color: $primaryOnBackground;
          border-radius: 999px;
          font-weight: 500 !important;
          position: relative;
          transition:
            background 0.18s ease,
            color 0.18s ease,
            border-color 0.18s ease,
            box-shadow 0.18s ease;

          &::before {
            content: "";
            width: 10px;
            height: 10px;
            border-radius: 50%;
            border: 2px solid $primary;
            box-sizing: border-box;
            display: inline-block;
          }
        }

        input[type="radio"]:checked + span {
          background: $primary;
          color: $textColorOnPrimary;
          border-color: $primary;

          &::before {
            background: $textColorOnPrimary;
            border-color: $textColorOnPrimary;
          }
        }

        input[type="radio"]:focus-visible + span {
          outline: 2px solid $primary;
          outline-offset: 2px;
        }

        &:hover span {
          border-color: $primary;
        }
      }
    }
  }

  .pricingContainer {
    display: flex;
    flex-direction: column;
    gap: 14px;
    width: 100%;

    @include media-breakpoint-up(lg) {
      width: 211px;
      flex: 0 0 211px;
      justify-content: center;
    }

    a {
      width: 100%;
      display: block;
      text-decoration: none;
    }

    [role="ui-button"] {
      width: 100%;
      border-radius: 100px;
      padding: 12px 16px;
      font-size: 16px;

      @include media-breakpoint-up(lg) {
        padding: 9px 16px;
        font-size: 14px;
      }
    }
  }

  .ctaGroup {
    display: flex;
    flex-direction: column;
    gap: 8px;

    // The operator's secondary "no availability" action reads as a text link,
    // not a third button. Needs to out-specify `.pricingContainer a`, which
    // blocks the anchor out full-width and strips its underline for the
    // button-wrapping links above.
    .secondaryAction {
      display: inline;
      width: auto;
      align-self: center;
      font-size: 14px;
      line-height: 1.4;
      color: $primaryOnBackground;
      text-decoration: underline;
      text-align: center;

      &:hover {
        text-decoration: underline;
      }
    }
  }
}

.spacer {
  flex: 1;
}

.bodyText {
  font-size: 14px;
  line-height: 1.4;
  color: $primaryOnBackground;
  margin-top: 12px;
}

// Selectable deal / price cards (SUL redesign "Price Info" component)
.priceInfoStack {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.priceInfo {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  padding: 0;
  background: #fff;
  border: 1px solid $primary50Pct;
  border-radius: 10px;
  overflow: hidden;
  text-align: left;
  transition:
    border-color 0.18s ease,
    box-shadow 0.18s ease;

  // Deal-card spans are also subject to the app root's span reset — force
  // alignment / line-height / weight so the price and header render correctly.
  .discountHeader {
    display: flex;
    align-items: center !important;
    gap: 7px;
    padding: 4px 10px;
    background: $primary50Pct;
    color: #0f0f0f;
  }

  .dealRadio {
    position: relative;
    flex-shrink: 0;
    width: 15px;
    height: 15px;
    border-radius: 50%;
    background: #fff;
    box-sizing: border-box;
  }

  .discountText {
    font-size: 10px;
    line-height: 1.5 !important;
  }

  .priceDetails {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 8px 10px;
  }

  .priceRow {
    display: flex;
    align-items: flex-end !important;
    gap: 2px;
  }

  .current {
    font-size: 25px;
    font-weight: 500 !important;
    line-height: 1.2 !important;
    color: #0f0f0f;
  }

  .period {
    font-size: 12px;
    font-weight: 500 !important;
    line-height: 1.4 !important;
    color: #434343;
  }

  .original {
    font-size: 10px;
    line-height: 1 !important;
    color: #808080;
    text-decoration: line-through;
  }

  .customPrimary {
    font-size: 12px;
    font-weight: 500 !important;
    line-height: 1.4 !important;
    color: #0f0f0f;
  }

  .customSecondary {
    font-size: 11px;
    line-height: 1.4 !important;
    color: #434343;
  }
}

button.priceInfo {
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid $primary;
    outline-offset: 2px;
  }

  &:hover {
    border-color: $primary;
  }
}

.priceInfoActive {
  border-color: $primary;

  .discountHeader {
    background: $primary;
    color: $textColorOnPrimary;
  }

  .dealRadio {
    background: transparent;
    border: 3px solid #fff;
  }
}

.priceInfoPlain {
  .priceDetails {
    padding-top: 12px;
  }
}

// Read-only summary card (details modal): no radio, centred label and a roomier
// type scale than the selectable listing cards. Must stay last so its overrides
// win over .priceInfoActive / .priceInfoPlain at equal specificity.
.priceInfoSummary {
  .discountHeader {
    justify-content: center;
    padding: 5px 15px;
  }

  .discountText {
    font-size: 12px;
  }

  .priceDetails {
    gap: 17px;
    padding: 15px;
  }

  .current {
    font-size: 20px;
  }
}

// No deal to frame: the price reads as plain text rather than a card. Last so it
// wins the border over .priceInfo / .priceInfoActive at equal specificity.
.priceInfoBorderless {
  border: none;
}
